import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileTextOutlined, QuestionCircleOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  TradevestDocLang,
  TradevestExchangeCode,
  TradevestPriceRange,
  TradevestTraditionalAssetDefinition,
  TradevestTraditionalAssetExchange,
  TradevestTraditionalAssetType,
  TradevestTraditionalTradingStatus,
} from '@mygate/common';
import { tradevestCatalogService } from '@/services/tradevest-catalog';
import { PriceLineChart } from './PriceLineChart';

const { Text } = Typography;

const PAGE_LIMIT = 50;

const TYPE_COLORS: Partial<Record<TradevestTraditionalAssetType, string>> = {
  [TradevestTraditionalAssetType.ETF]: 'green',
  [TradevestTraditionalAssetType.EQUITY]: 'blue',
  [TradevestTraditionalAssetType.BOND]: 'gold',
  [TradevestTraditionalAssetType.FUND]: 'purple',
  [TradevestTraditionalAssetType.DERIVATIVE]: 'magenta',
  [TradevestTraditionalAssetType.ETN]: 'cyan',
};

const RISK_COLORS: Record<string, string> = { '1': 'green', '2': 'gold', '3': 'red' };

const RANGE_OPTIONS = [
  { label: '1T', value: TradevestPriceRange.ONE_DAY },
  { label: '1M', value: TradevestPriceRange.ONE_MONTH },
  { label: '3M', value: TradevestPriceRange.THREE_MONTHS },
  { label: '1J', value: TradevestPriceRange.ONE_YEAR },
  { label: 'Max', value: TradevestPriceRange.MAX },
];

function statusColor(status: TradevestTraditionalTradingStatus): string {
  return status === TradevestTraditionalTradingStatus.ACTIVE ? 'success' : 'default';
}

function renderExchanges(exchanges: TradevestTraditionalAssetExchange[]) {
  return (
    <Space size={4} wrap>
      {exchanges.map(ex => (
        <Tag key={ex.exchangeCode} color={statusColor(ex.tradingStatus)} style={{ margin: 0 }}>
          {ex.exchangeCode} · {ex.tradingStatus === TradevestTraditionalTradingStatus.ACTIVE ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ))}
    </Space>
  );
}

/** TER arrives as a decimal STRING (`"0.2"`), not a number — see MYG-548. */
function formatTer(ter?: string): string {
  if (ter === undefined || ter === null || ter === '') return '—';
  return `${ter}%`;
}

/** Per-instrument detail: metadata + price chart (EODHD) + KID download. */
function InstrumentDetail({ asset }: { asset: TradevestTraditionalAssetDefinition }) {
  const { message } = App.useApp();
  const [range, setRange] = useState<TradevestPriceRange>(TradevestPriceRange.ONE_MONTH);
  const [lang, setLang] = useState<TradevestDocLang>('de');
  const [downloading, setDownloading] = useState(false);

  const { data: history, isLoading } = useQuery({
    queryKey: ['tv-catalog-history', asset.isin, range],
    queryFn: async () => (await tradevestCatalogService.getHistory(asset.isin, range)).data?.history ?? null,
    staleTime: 1000 * 60,
  });

  const handleDownloadKid = async () => {
    setDownloading(true);
    try {
      const blob = await tradevestCatalogService.downloadKid(asset.isin, lang);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kid_${asset.isin}_${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('KID downloaded');
    } catch (error) {
      message.warning(
        error instanceof Error && error.message
          ? `KID unavailable: ${error.message}`
          : 'KID unavailable for this instrument',
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="ISIN">
          <Text copyable>{asset.isin}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="WKN">{asset.wkn || '—'}</Descriptions.Item>
        <Descriptions.Item label="Name">{asset.assetName}</Descriptions.Item>
        <Descriptions.Item label="Type">
          <Tag color={TYPE_COLORS[asset.assetType]}>{asset.assetType}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Risk score">
          <Tag color={RISK_COLORS[asset.assetRiskScore]}>{asset.assetRiskScore} / 3</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Fractional trading">{asset.fractionalTrading ? 'Yes' : 'No'}</Descriptions.Item>
        {/* Upstream's real field name. Until MYG-548 this read `savingsPlan`, which the API never
            sends, so it showed "No" for every instrument. The `(min €X)` suffix is gone too —
            `savingsPlanMinAmount` is not on this endpoint. */}
        <Descriptions.Item label="Savings plan">{asset.savingsPlansEligible ? 'Yes' : 'No'}</Descriptions.Item>
        <Descriptions.Item label="TER">{formatTer(asset.totalExpenseRatio)}</Descriptions.Item>
        <Descriptions.Item label="Exchanges">{renderExchanges(asset.exchanges)}</Descriptions.Item>
      </Descriptions>

      <div>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text strong>Price history</Text>
          <Segmented options={RANGE_OPTIONS} value={range} onChange={v => setRange(v as TradevestPriceRange)} />
        </Space>
        <Card size="small" styles={{ body: { padding: 8 } }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin />
            </div>
          ) : history && history.points.length > 0 ? (
            <PriceLineChart points={history.points} currency={history.currency} />
          ) : (
            <Empty description="No chart data for this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Key Information Document (KID)
        </Text>
        {asset.assetType === TradevestTraditionalAssetType.ETF ? (
          <Space wrap>
            <Select
              value={lang}
              style={{ width: 100 }}
              onChange={setLang}
              options={[
                { label: 'DE', value: 'de' },
                { label: 'EN', value: 'en' },
              ]}
            />
            <Button icon={<FileTextOutlined />} loading={downloading} onClick={handleDownloadKid}>
              Download KID
            </Button>
          </Space>
        ) : (
          <Text type="secondary">A PRIIPs KID exists only for ETFs — not for {asset.assetType.toLowerCase()}s.</Text>
        )}
      </div>
    </Space>
  );
}

/**
 * Read-only browser for the live Tradevest traditional-asset catalog (MYG-397). Lives as a tab inside
 * Asset Management; complements the curated internal catalog with the full third-party universe.
 */
export function TradevestCatalogBrowser() {
  const [assetType, setAssetType] = useState<string | undefined>();
  const [tradingStatus, setTradingStatus] = useState<string | undefined>(TradevestTraditionalTradingStatus.ACTIVE);
  const [exchangeCode, setExchangeCode] = useState<string | undefined>();
  const [savingsPlan, setSavingsPlan] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Cursor pagination — Tradevest is cursor-based, so we keep a stack of prior-page cursors for "Prev".
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);

  const [selected, setSelected] = useState<TradevestTraditionalAssetDefinition | null>(null);

  const resetPagination = () => {
    setCursor(undefined);
    setCursorHistory([]);
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['tv-catalog', assetType, tradingStatus, exchangeCode, savingsPlan, search, cursor],
    queryFn: async () => {
      const res = await tradevestCatalogService.listInstruments({
        assetType,
        tradingStatus,
        exchangeCode,
        savingsPlansEligible: savingsPlan === undefined ? undefined : savingsPlan === 'true',
        search: search || undefined,
        limit: PAGE_LIMIT,
        cursor,
      });
      return res.data;
    },
    staleTime: 1000 * 60,
  });

  const applySearch = () => {
    resetPagination();
    setSearch(searchInput.trim());
  };

  const onFilterChange = (setter: (v: string | undefined) => void, value: string | undefined) => {
    resetPagination();
    setter(value);
  };

  const goNext = () => {
    if (!data?.pagination.hasMore) return;
    setCursorHistory(prev => [...prev, cursor]);
    setCursor(data.pagination.cursor);
  };

  const goPrev = () => {
    if (cursorHistory.length === 0) return;
    const prevCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory(prev => prev.slice(0, -1));
    setCursor(prevCursor);
  };

  const searchActive = search.length > 0;

  const columns: ColumnsType<TradevestTraditionalAssetDefinition> = [
    {
      title: 'ISIN',
      dataIndex: 'isin',
      key: 'isin',
      width: 140,
      render: (isin: string) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{isin}</Text>,
    },
    { title: 'WKN', dataIndex: 'wkn', key: 'wkn', width: 90, render: (wkn: string) => wkn || '—' },
    { title: 'Name', dataIndex: 'assetName', key: 'assetName', ellipsis: true },
    {
      title: 'Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 110,
      render: (type: TradevestTraditionalAssetType) => <Tag color={TYPE_COLORS[type]}>{type}</Tag>,
    },
    {
      title: 'Risk',
      dataIndex: 'assetRiskScore',
      key: 'assetRiskScore',
      width: 70,
      align: 'center',
      render: (risk: string) => <Tag color={RISK_COLORS[risk]}>{risk}</Tag>,
    },
    {
      title: 'Savings plan',
      dataIndex: 'savingsPlansEligible',
      key: 'savingsPlansEligible',
      width: 110,
      align: 'center',
      render: (sp: boolean) => (sp ? <Tag color="blue">Yes</Tag> : <Text type="secondary">No</Text>),
    },
    { title: 'TER', key: 'ter', width: 80, align: 'right', render: (_, r) => formatTer(r.totalExpenseRatio) },
    {
      title: 'Exchanges',
      key: 'exchanges',
      width: 220,
      render: (_, r) => renderExchanges(r.exchanges),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => setSelected(r)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {data && (
        <Alert
          type={data.environment === 'production' ? 'warning' : 'info'}
          showIcon
          message={
            <Space wrap size={6}>
              <span>
                Live Tradevest catalog · {String(data.environment).toUpperCase()} · {data.host}
              </span>
              <Tooltip
                title={`Internal Tradevest config slot: "${data.provider}" (env-configured, points at ${data.host})`}
              >
                <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            </Space>
          }
        />
      )}

      <Card size="small">
        <Space size={12} wrap>
          <Input
            placeholder="Search name / ISIN / WKN…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onPressEnter={applySearch}
            allowClear
            onClear={() => {
              setSearchInput('');
              resetPagination();
              setSearch('');
            }}
            style={{ width: 280 }}
          />
          <Button type="primary" onClick={applySearch}>
            Search
          </Button>
          <Select
            placeholder="Type"
            allowClear
            style={{ width: 140 }}
            value={assetType}
            disabled={searchActive}
            onChange={v => onFilterChange(setAssetType, v)}
            options={Object.values(TradevestTraditionalAssetType).map(t => ({ label: t, value: t }))}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 130 }}
            value={tradingStatus}
            disabled={searchActive}
            onChange={v => onFilterChange(setTradingStatus, v)}
            options={Object.values(TradevestTraditionalTradingStatus).map(s => ({ label: s, value: s }))}
          />
          <Select
            placeholder="Exchange"
            allowClear
            style={{ width: 120 }}
            value={exchangeCode}
            disabled={searchActive}
            onChange={v => onFilterChange(setExchangeCode, v)}
            options={Object.values(TradevestExchangeCode).map(x => ({ label: x, value: x }))}
          />
          <Select
            placeholder="Savings plan"
            allowClear
            style={{ width: 140 }}
            value={savingsPlan}
            disabled={searchActive}
            onChange={v => onFilterChange(setSavingsPlan, v)}
            options={[
              { label: 'Savings plan: Yes', value: 'true' },
              { label: 'Savings plan: No', value: 'false' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
        </Space>
        {searchActive && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            Free-text search runs over the cached <b>ACTIVE</b> universe (ETF + equities). Clear search to filter the
            full catalog incl. inactive instruments.
          </Text>
        )}
      </Card>

      {error ? (
        <Alert type="error" showIcon message="Failed to load Tradevest catalog" description={String(error)} />
      ) : (
        <Card size="small" styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="isin"
            columns={columns}
            dataSource={data?.assets ?? []}
            loading={isLoading}
            pagination={false}
            size="small"
            scroll={{ x: 980 }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderTop: '1px solid #f0f0f0',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data ? `${data.assets.length} shown${data.pagination.hasMore ? ' · more available' : ''}` : ''}
            </Text>
            <Space wrap>
              <Button size="small" disabled={cursorHistory.length === 0 || isFetching} onClick={goPrev}>
                Prev
              </Button>
              <Button size="small" disabled={!data?.pagination.hasMore || isFetching} onClick={goNext}>
                Next
              </Button>
            </Space>
          </div>
        </Card>
      )}

      <Drawer
        title={selected ? selected.assetName : ''}
        width={680}
        open={!!selected}
        onClose={() => setSelected(null)}
        destroyOnClose
      >
        {selected && <InstrumentDetail asset={selected} />}
      </Drawer>
    </Space>
  );
}
